<template>
    <div>
        <div class="modal-overlay active" @click="closeDialog"></div>
        <div class="modal">
            <div class="modal__header">
                <h2 class="headline-s">Create Token</h2>
                <CloseIcon class="modal__header--close" alt="Close modal" aria-label="Close modal" @click="closeDialog" />
            </div>
            <div class="modal__body flex-column">
                <form class="token-form" @submit.prevent="handleSubmit">
                    <div class="input-group">
                        <label for="tokenNameModal" class="label">Token name</label>
                        <input
                            id="tokenNameModal"
                            v-model="tokenName"
                            type="text"
                            name="token-name"
                            placeholder="e.g. Partner integration"
                        />
                        <p v-if="error" class="input-support-text invalid-state">
                            {{ error }}
                        </p>
                        <p v-else class="input-support-text">Add a label so you remember where this token is used.</p>
                    </div>

                    <div class="input-group">
                        <p class="label">Environment</p>
                        <div class="radio-group" role="radiogroup" aria-label="API token environment">
                            <label class="radio-option" :class="{ 'radio-option--active': mode === 'sandbox' }" for="tokenModeSandbox">
                                <input
                                    id="tokenModeSandbox"
                                    v-model="mode"
                                    type="radio"
                                    name="token-mode"
                                    value="sandbox"
                                />
                                <div class="radio-option__content">
                                    <span class="radio-option__title">Sandbox ordering</span>
                                    <span class="input-support-text">Use for testing without affecting live orders.</span>
                                </div>
                            </label>
                            <label class="radio-option" :class="{ 'radio-option--active': mode === 'live' }" for="tokenModeLive">
                                <input
                                    id="tokenModeLive"
                                    v-model="mode"
                                    type="radio"
                                    name="token-mode"
                                    value="live"
                                />
                                <div class="radio-option__content">
                                    <span class="radio-option__title">Live ordering</span>
                                    <span class="input-support-text">Use when you are ready to place real orders.</span>
                                </div>
                            </label>
                        </div>
                    </div>


                    <div v-if="createdToken" class="token-display">
                        <p class="paragraph-details strong">Copy and store this token now. You will not be able to see it again.</p>
                        <div @click="copyToClipboard" class="token-display__value" aria-label="New API token" role="button">
                            <ClipboardIcon class="token-display__icon" />
                            {{ createdToken }}
                        </div>
                        <div v-if="copied" class="input-support-text">Copied to clipboard!</div>
                    </div>
                    <div class="token-form__actions">
                        <RothcoCta type="button" class="secondary" :button_icon="null" aria-label="Cancel create token" @click.prevent="closeDialog">
                            Close
                        </RothcoCta>
                        <RothcoCta
                            type="submit"
                            class="primary"
                            button_icon="plus"
                            :disabled="submitting"
                            aria-label="Create API token"
                        >
                            {{ createdToken ? 'Create another' : 'Create token' }}
                        </RothcoCta>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { inject, ref } from 'vue';
import RothcoCta from '@/components/RothcoCta.vue';
import CloseIcon from '@icons/closeIcon.svg';
import ClipboardIcon from '@icons/clipboardIcon.svg';

type TokenMode = 'sandbox' | 'live';
type ModalData = {
    onSubmit?: (
        name: string,
        mode?: TokenMode
    ) => Promise<string | { error?: string; plainTextToken?: string } | { plainTextToken?: string } | void> | string | { error?: string; plainTextToken?: string } | { plainTextToken?: string } | void;
};

const dialogRef = inject<any>('dialogRef');
const tokenName = ref('');
const mode = ref<TokenMode>('sandbox');
const error = ref('');
const submitting = ref(false);
const createdToken = ref('');

const onSubmit = (dialogRef?.value?.data as ModalData | undefined)?.onSubmit;

function closeDialog() {
    dialogRef?.value?.close();
}

async function handleSubmit() {
    if (submitting.value) return;
    submitting.value = true;
    error.value = '';

    try {
        const result = await onSubmit?.(tokenName.value, mode.value);

        if (typeof result === 'string' && result) {
            error.value = result;
            return;
        }

        if (result && typeof result === 'object' && 'error' in result && result.error) {
            error.value = result.error ?? '';
            return;
        }

        const plainTextToken =
            (result as any)?.plain_text_token ?? (result as any)?.plainTextToken;
        if (plainTextToken) {
            createdToken.value = plainTextToken;
        } else {
            dialogRef?.value?.close();
        }
        tokenName.value = '';
    } finally {
        submitting.value = false;
    }
}

let copied = ref(false);

function copyToClipboard() {
    if (!createdToken.value) return;
    navigator.clipboard.writeText(createdToken.value);
    copied.value = true;
    setTimeout(() => {
        copied.value = false;
    }, 2000);
}
</script>

<style scoped lang="scss">
@import "../assets/styles/variables.scss";

.token-form {
    display: grid;
    gap: 0.75rem;
    margin-top: 0.75rem;
}

.input-group {
    display: grid;
    gap: 0.35rem;
}

.input-group input[type="text"] {
    border: 1px solid $rothcoBorder;
    border-radius: 0.375rem;
    padding: 0.75rem 0.85rem;
    font-size: 1rem;
    color: $rothcoCharcoal;
}

.input-group input[type="text"]:focus {
    outline: 2px solid $rothcoRed;
    border-color: $rothcoRed;
}

.input-support-text {
    font-size: 0.875rem;
    color: $rothcoText;
}

.token-form__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
}

.invalid-state {
    color: $rothcoRed;
}

.token-display {
    display: grid;
    gap: 0.5rem;
    background: $rothcoCream;
    border: 1px solid $rothcoBorder;
    border-radius: 0.5rem;
    padding: 0.75rem 0.85rem;
}

.token-display__value {
    cursor: pointer;
    font-family: monospace;
    word-break: break-all;
    background: $rothcoWhite;
    border: 1px dashed $rothcoBorder;
    border-radius: 0.35rem;
    padding: 0.6rem 0.7rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.token-display__icon {
    flex-shrink: 0;
    width: 1.25rem;
    height: 1.25rem;
    color: $rothcoRed;
}

.strong {
    font-weight: 600;
}

.radio-group {
    display: grid;
    gap: 0.5rem;
}

.radio-option {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    padding: 0.65rem 0.75rem;
    border: 1px solid $rothcoBorder;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease;
}

.radio-option input {
    margin-top: 0.25rem;
}

.radio-option__content {
    display: grid;
    gap: 0.2rem;
}

.radio-option__title {
    font-weight: 600;
    color: $rothcoCharcoal;
}

.radio-option--active {
    border-color: $rothcoRed;
    background-color: rgba($rothcoRed, 0.05);
}
</style>
